// Vendor
import { Router } from 'vue-router';

// Personal libs
import { dbGetActiveUser } from '@data/datasource/local/user.database';
import { RepositoryStrategyEnum } from '@shared/enum/repository-strategy.enum';
import { SessionStorageEnum } from '@shared/enum/sessionstorage.enum';
import { UpdateManagerUi } from '@shared/service/updateManager/update-manager-ui.service';
import { UpdateManagerProgram } from '@shared/service/updateManager/update-manager-program.service';
import { UpdateManagerUser } from '@shared/service/updateManager/update-manager-user.service';
import { closeAllModals, loggoutActions, redirectOnLoggout } from './loggout.lib';
import {
  updateCachedVideoList,
  checkAndUpdateVideoSize,
} from '@shared/lib/service-worker.lib';
import { AuthResponseDto } from '@data/dto/auth-response.dto';
import { RoleEnum } from '@shared/enum/role.enum';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { IAuthRequest } from '@shared/interface/auth-request.interface';
import { hashPassword } from './crypto.lib';

export interface ILogginActionsParam {
  hasToGetUserSettings?: boolean;
  apiResponse?: IApiResponse<AuthResponseDto, string>;
  authResponseDto?: AuthResponseDto;
  displayLoader?: boolean;
  router?: Router;
}
export const loginActions = async (params?: ILogginActionsParam) => {
  // Init user
  const activeUser = await sessionManager.setCurrentUser({
    apiResponse: params?.apiResponse,
    authResponseDto: params?.authResponseDto,
  });

  // Refresh changelog
  new UpdateManagerUi.UiChangelog().exec(RepositoryStrategyEnum.NETWORK_FIRST);

  // Refresh programs
  new UpdateManagerProgram.ProgramAll().exec(
    RepositoryStrategyEnum.NETWORK_FIRST,
  );

  // Refresh config
  new UpdateManagerUi.UiConfigAll().exec(RepositoryStrategyEnum.NETWORK_FIRST);

  // Refresh Users (if connected user is admin or super admin)
  const user = activeUser;
  if (
    user != null &&
    (user?.roles?.includes(RoleEnum.ROLE_ADMIN) ||
      user?.roles?.includes(RoleEnum.ROLE_SUPER_ADMIN))
  ) {
    new UpdateManagerUser.UserAll().exec(RepositoryStrategyEnum.NETWORK_FIRST);
  }

  // Init Reload detector
  sessionStorage.setItem(
    SessionStorageEnum.RELOAD_DETECETOR,
    SessionStorageEnum.RELOAD_DETECETOR,
  );

  // Check size of cachedVideo and update it if necessary
  checkAndUpdateVideoSize().then(() => {
    // Update cached video list
    updateCachedVideoList();
  });
};

/**
 * Function used to detect the state of the session
 * Purpose : check if the user is already connected
 * Tricks:
 * - SessionStorage is not cleared when the page is reloaded.
 * - But SessionStorage is cleared when the browser or the tab is closed
 */
export const refreshLoginData = () => {
  dbGetActiveUser().then((user) => {
    if (user?.isConnected === true) {
      if (
        user.chkRemember === true ||
        (!user.chkRemember &&
          sessionStorage.getItem(SessionStorageEnum.RELOAD_DETECETOR) != null)
      ) {
        sessionManager.currentUser.value = user;

        // Login actions
        loginActions();
      } else {
        // Disconnect user
        if (!user.chkRemember) {
          loggoutActions({ from: 'refreshLoginData', showLoggoutNotif: false })
            .then(() => closeAllModals())
            .then(() => redirectOnLoggout())
        }

        sessionStorage.setItem(
          SessionStorageEnum.RELOAD_DETECETOR,
          SessionStorageEnum.RELOAD_DETECETOR,
        );
      }
    }
  });
};

export const prepareAuthPayload = async (params: IAuthRequest): Promise<IAuthRequest> => {
  return {
    email: params.email?.toLocaleLowerCase(),
    password: hashPassword({
      email: params.email?.toLocaleLowerCase(),
      readablePassword: params.password
    }),
    chkRemember: params.chkRemember === true,
    fingerprint: params.fingerprint
  }
}
